import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import Plotly from 'plotly.js-dist-min';

type DiseaseId = 'FMD' | 'LSD' | 'SGP' | 'PPR';
type SpeciesId = 'cattle' | 'sheep' | 'goat' | 'buffalo';

const DISEASES: { id: DiseaseId; label: string }[] = [
  { id: 'FMD', label: 'FMD' },
  { id: 'LSD', label: 'LSD' },
  { id: 'SGP', label: 'SGP' },
  { id: 'PPR', label: 'PPR' },
];

/** Species relevant to clinical surveillance per disease */
const DISEASE_SPECIES: Record<DiseaseId, { id: SpeciesId; label: string; census: string; exam: string; pos: string }[]> = {
  FMD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', exam: 'cattle_exam', pos: 'cattle_pos_fmd' },
    { id: 'sheep', label: 'Sheep', census: 'sheep', exam: 'sheep_exam', pos: 'sheep_pos_fmd' },
    { id: 'goat', label: 'Goats', census: 'goat', exam: 'goat_exam', pos: 'goat_pos_fmd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', exam: 'buffalo_exam', pos: 'buffalo_pos_fmd' },
  ],
  LSD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', exam: 'cattle_exam', pos: 'cattle_pos_lsd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', exam: 'buffalo_exam', pos: 'buffalo_pos_lsd' },
  ],
  SGP: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', exam: 'sheep_exam', pos: 'sheep_pos_sgp' },
    { id: 'goat', label: 'Goats', census: 'goat', exam: 'goat_exam', pos: 'goat_pos_sgp' },
  ],
  PPR: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', exam: 'sheep_exam', pos: 'sheep_pos_ppr' },
    { id: 'goat', label: 'Goats', census: 'goat', exam: 'goat_exam', pos: 'goat_pos_ppr' },
  ],
};

const COLORS = {
  positive: '#f5b4b4',
  examinedNeg: '#a8d4ef',
  notExamined: '#e5e7eb',
};

function n(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
}

interface NestRow {
  label: string;
  area: string;
  species: string;
  census: number;
  exam: number;
  positive: number;
  examinedNeg: number;
  notExamined: number;
}

function buildNestRows(clinical: any[], disease: DiseaseId): NestRow[] {
  const specs = DISEASE_SPECIES[disease];
  const out: NestRow[] = [];
  for (const row of clinical || []) {
    const area = String(row.district_province || row.district || '—');
    for (const sp of specs) {
      const census = n(row[sp.census]);
      let exam = n(row[sp.exam]);
      let positive = n(row[sp.pos]);
      // Keep nested parts inside the whole
      if (exam > census && census > 0) exam = census;
      if (positive > exam) positive = exam;
      if (census <= 0 && exam <= 0 && positive <= 0) continue;
      const total = Math.max(census, exam, positive);
      const examCap = Math.min(exam, total);
      const posCap = Math.min(positive, examCap);
      out.push({
        label: `${area} · ${sp.label}`,
        area,
        species: sp.label,
        census: total,
        exam: examCap,
        positive: posCap,
        examinedNeg: Math.max(0, examCap - posCap),
        notExamined: Math.max(0, total - examCap),
      });
    }
  }
  return out;
}

export interface CycleClinicalVizProps {
  clinical: any[];
  quarter: number;
  year: number;
}

const CycleClinicalViz: React.FC<CycleClinicalVizProps> = ({ clinical, quarter, year }) => {
  const [disease, setDisease] = useState<DiseaseId>('FMD');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const nestRows = useMemo(() => buildNestRows(clinical, disease), [clinical, disease]);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    if (!nestRows.length) {
      Plotly.purge(el);
      return;
    }

    // Plotly stacked horizontal: order of traces = left → right
    // Positive (inside examined) | Examined-but-negative | Not examined
    // Sum = census (the whole)
    const y = nestRows.map((r) => r.label);
    const hoverBase = nestRows.map((r) => {
      const examPct = r.census > 0 ? ((r.exam / r.census) * 100).toFixed(1) : '—';
      const posPctExam = r.exam > 0 ? ((r.positive / r.exam) * 100).toFixed(1) : '—';
      const posPctAll = r.census > 0 ? ((r.positive / r.census) * 100).toFixed(1) : '—';
      return (
        `<b>${r.area}</b> · ${r.species}<br>` +
        `Census: ${r.census.toLocaleString()}<br>` +
        `Examined: ${r.exam.toLocaleString()} (${examPct}% of census)<br>` +
        `Positive (${disease}): ${r.positive.toLocaleString()} (${posPctExam}% of exam, ${posPctAll}% of census)`
      );
    });

    const traces = [
      {
        type: 'bar',
        orientation: 'h',
        name: `Positive (${disease})`,
        y,
        x: nestRows.map((r) => r.positive),
        marker: { color: COLORS.positive, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: nestRows.map((r) => (r.positive > 0 ? String(r.positive) : '')),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Examined (no clinical +)',
        y,
        x: nestRows.map((r) => r.examinedNeg),
        marker: { color: COLORS.examinedNeg, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: nestRows.map((r) => (r.examinedNeg > 0 ? String(r.examinedNeg) : '')),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Not examined',
        y,
        x: nestRows.map((r) => r.notExamined),
        marker: { color: COLORS.notExamined, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: nestRows.map((r) => (r.notExamined > 0 ? String(r.notExamined) : '')),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#6b7280' },
      },
    ];

    const rowH = 28;
    const height = Math.max(280, 80 + nestRows.length * rowH);

    Plotly.newPlot(
      el,
      traces,
      {
        barmode: 'stack',
        margin: { t: 16, r: 24, b: 48, l: 160 },
        height,
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { size: 12, color: '#374151' },
        xaxis: {
          title: { text: 'Number of animals (full bar is the census)', font: { size: 12 } },
          gridcolor: '#f3f4f6',
          zeroline: false,
        },
        yaxis: {
          autorange: 'reversed',
          tickfont: { size: 11 },
        },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0,
          font: { size: 11 },
        },
        bargap: 0.25,
      },
      { responsive: true, displayModeBar: false },
    );

    return () => {
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
    };
  }, [nestRows, disease]);

  return (
    <div className="space-y-4">
      <h3 className="text-2xl font-black text-gray-800 bg-red-100 px-4 py-3 rounded">
        Clinical Examination — Quarter {quarter}, {year}
      </h3>

      <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
        The full length of each bar is the number of animals present (census). Within that, the
        coloured segments show how many were clinically examined, and within the examined animals,
        how many were positive for the selected disease. The three segments always add up to the
        census.
      </p>

      <div className="flex flex-wrap gap-2">
        {DISEASES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDisease(d.id)}
            className={`nav-btn px-4 py-1.5 text-sm ${
              disease === d.id ? 'ring-2 ring-green-600 ring-offset-1' : 'opacity-80'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.positive }} />
            Positive ({disease})
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.examinedNeg }} />
            Examined, no clinical +
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm border border-gray-200" style={{ backgroundColor: COLORS.notExamined }} />
            Not examined
          </span>
        </div>

        {nestRows.length ? (
          <div ref={chartRef} style={{ width: '100%' }} />
        ) : (
          <div className="h-48 flex items-center justify-center text-sm text-gray-500">
            No clinical census / exam data for {disease} in this quarter.
          </div>
        )}
      </div>
    </div>
  );
};

export default CycleClinicalViz;
